/**
 * @author cai
 */
export default class SocketManager {
	/**
	 * @param {String} taskName；当前创建的socket的任务名
	 * @param {Boolean} closeLog；关闭所有的console.log
	 */
	#taskMap;	// socket任务对象
	#taskName;	// socket任务名称
	#closeLog;	// 关闭所有console.log
	constructor(taskName, closeLog = false) {
		if (!taskName) {
			throw new Error("请传入当前创建的socket的任务名，类型是字符串");
		}
		if (typeof taskName != "string") {
			throw new Error("传入的任务名类型须是字符串");
		}
		this.#taskMap = {};
		this.#taskName = taskName;
		this.#closeLog = closeLog;
	}
	/**
	 * @description 打印
	 * @param {String} content；打印内容
	 * @param {Boolean} close；关闭打印
	 */
	#log(content, close) {
		if (!close && !this.#closeLog) {
			console.log(content);
		}
	}
	/**
	 * @description 重连socket
	 */
	#reconnectCount = 10;	// 重连次数，用于重置
	#connectParams = {};	// connect函数的参数
	#reconnectParams = {
		timer: null,	// 重连定时器
		isExecute: true,// 是否可以重连
		count: 10,		// 重连次数
		time: 10000		// 多少毫秒重连一次
	};
	// 设置重连参数
	setReconnectParams(params) {
		this.#reconnectParams = {...this.#reconnectParams, ...params};
		this.#reconnectCount = this.#reconnectParams.count;
	}
	// 重置重连相关数据
	#resetReconnectData() {
		if (this.#reconnectParams.timer) {
			clearTimeout(this.#reconnectParams.timer);
			this.#reconnectParams.timer = null;
		}
		this.#reconnectParams.count = this.#reconnectCount;
	}
	// 重连socket
	#reconnect() {
		if (!this.#reconnectParams.isExecute || this.#reconnectParams.timer) return;
		console.log('重连socket');
		this.#reconnectParams.timer = setTimeout(() => {
			this.#reconnectParams.timer = null; // 立即清理，防止多次重连
			if (this.#reconnectParams.count > 0) {
				this.#reconnectParams.count -= 1;
				this.connect(this.#connectParams);
			}
		}, this.#reconnectParams.time)
	}
	/**
	 * @description 心跳
	 */
	#heartbeatParams = {
		open: true,		// 是否开启心跳
		time: 30000,	// 多少毫秒发送心跳
		data: "",		// 心跳发送的内容
	}
	// 设置心跳的参数
	setHeartbeatParams(params) {
		this.#heartbeatParams = {...this.#heartbeatParams, ...params};
	}
	// 发送心跳
	#sendHeartbeat() {
		// 需要开启心跳
		if (this.#heartbeatParams.open) {
			this.#taskMap[`${this.#taskName}HeartbeatTimer`] = setInterval(() => {
				this.send(this.#heartbeatParams.data, true);
			}, this.#heartbeatParams.time)
		}
	}
	// 清除心跳定时器
	#clearHeartbeatTimer() {
		if (this.#taskMap[`${this.#taskName}HeartbeatTimer`]) {
			clearInterval(this.#taskMap[`${this.#taskName}HeartbeatTimer`]);
			this.#taskMap[`${this.#taskName}HeartbeatTimer`] = undefined;
		}
	}
	/**
	 * @description 连接socket
	 * ========================
	 * @param {Object} = params
	 * params包含以下参数
	 * @param {String} = url；uni.connectSocket的url参数
	 * @param {Object} = connectParams；uni.connectSocket所需的参数
	 * @param {Function} = onOpen；SocketTask.onOpen，监听 WebSocket 连接打开事件
	 * @param {Function} = onMessage；SocketTask.onMessage，监听 WebSocket 接受到服务器的消息事件
	 * @param {Function} = onClose；SocketTask.onClose，监听 WebSocket 连接关闭事件
	 * @param {Function} = onError；SocketTask.onError，监听 WebSocket 错误事件
	 * ========================
	 */
	connect(params = {}) {
		if (!params.url && !params?.connectParams?.url) {
			throw new Error("请传入uni.connectSocket的必填参数url");
		}
		this.#connectParams = params;
		this.close(() => {
			if (!this.#taskMap[this.#taskName]) {
				this.#taskMap[this.#taskName] = uni.connectSocket({
					url: params.url,
					...params.connectParams,
					success: (res) => {
						this.#log(`${this.#taskName}的socket创建成功：${res}`);
					},
					fail: (err) => {
						this.#log(`${this.#taskName}的socket创建失败：${err}`);
					}
				})
				// 监听 WebSocket 连接打开事件
				this.#taskMap[this.#taskName].onOpen((res) => {
					this.#log(`${this.#taskName}的socket监听打开：${JSON.stringify(res)}`);
					this.#resetReconnectData(); // 只在 onOpen 时重置重连计数
					this.#sendHeartbeat();
					params.onOpen && params.onOpen(res);
				})
				// 监听 WebSocket 接受到服务器的消息事件
				this.#taskMap[this.#taskName].onMessage(res => {
					params.onMessage && params.onMessage(res);
				})
				// 监听 WebSocket 连接关闭事件
				this.#taskMap[this.#taskName].onClose((res) => {
					this.#log(`${this.#taskName}的socket监听关闭：${JSON.stringify(res)}`);
					this.#clearHeartbeatTimer();
					params.onClose && params.onClose(res);
					if (res.code != 1000) {
						this.#reconnect();
					}
				})
				// 监听 WebSocket 错误事件
				this.#taskMap[this.#taskName].onError((err) => {
					this.#log(`${this.#taskName}的socket监听错误：${JSON.stringify(err)}`);
					this.close();
					this.#clearHeartbeatTimer();
					params.onError && params.onError(err);
					this.#reconnect();
				})
			}
		});
	}
	/**
	 * @description 发送消息
	 * @param {String|ArrayBuffer} data；发送给服务的的内容
	 * @param {Boolean} closeLog；关闭提示
	 */
	send(data, closeLog) {
		if (this.#taskMap[this.#taskName]) {
			this.#taskMap[this.#taskName].send({
				data,
				success: () => {
					this.#log(`${this.#taskName}的消息发送成功，发送的内容：${data}`, closeLog);
				},
				fail: (err) => {
					this.#log(`${this.#taskName}的消息发送失败，发送的内容：${data}`, closeLog);
				}
			})
		}
	}
	/**
	 * @description 清除当前连接的SocketTask
	 */
	#clear() {
		if (this.#taskMap[`${this.#taskName}HeartbeatTimer`]) {
			clearInterval(this.#taskMap[`${this.#taskName}HeartbeatTimer`]);
		}
		delete this.#taskMap[this.#taskName];
	}
	/**
	 * @description 关闭当前socket
	 * @param {Function} callback;
	 */
	close(callback) {
		if (this.#taskMap[this.#taskName]) {
			this.#taskMap[this.#taskName].close({
				success: (res) => {
					this.#log(`关闭了${this.#taskName}的socket：${JSON.stringify(res)}`);
					this.#clear();
					callback && callback({message: "当前socket任务已关闭"});
				}
			})
		} else {
			callback && callback({errMsg: "当前socket任务不存在"});
		}
	}
}